/**
 * What-If PREPARING canvas state (design doc executive-view-design/
 * EXEC_WHATIF_v1_design.md §4 PREPARING row, §11.8 escape hatch).
 *
 * Live stage list (B2, 2026-09-05): propose is now kickoff-then-poll (like
 * approve), so this state receives the run's `progress` from the poll and
 * renders which pipeline stage is running — "Selecting segments" ->
 * "Planning changes" — instead of a bare spinner. The escape hatch §11.8
 * anticipated exactly this ("convert propose to kickoff-then-poll, stamp
 * phase markers"). The stage marker is `progress.propose_phase`, stamped by
 * the Agentic-API pipeline (whatif_pipeline._run_propose_after_plan_created).
 *
 * Rendering (2026-09-07 ruling): the stages are rows in the same CalcStatus-based ProcessingPanel every other Exec
 * wait uses — done / running / not-yet-started icons per row — replacing this file's own checklist so the four
 * processing states read as one idiom. Until the first poll returns a marker (or if progress is absent/malformed),
 * the panel shows spinner + title only — never a blank or broken canvas.
 */
import React from 'react';

import { CalcProgressRow } from 'src/components/AgenticV2/types';

import ProcessingPanel from '../ProcessingPanel';
import { WhatIfProgress } from '../types';

interface PreparingStateProps {
  progress?: WhatIfProgress | null;
}

const PREPARING_HEADING = 'Preparing proposed changes…';

// The propose pipeline's ordered LLM stages, with user-facing labels. Order
// matches whatif_pipeline: segment selection, then patch planning.
const STAGES: Array<{ key: string; label: string }> = [
  { key: 'segment_selection', label: 'Selecting segments' },
  { key: 'patch_planning', label: 'Planning changes' },
];

// Stages projected onto CalcStatus rows: the row's numeric id is the stage index (no RowIdentifier here), the
// operation counts stay 0 so no progress bar renders, and 'pending' is the FE-only "not started yet" status.
const stageRows = (activeIndex: number): CalcProgressRow[] =>
  STAGES.map((stage, i) => ({
    row_identifier_id: i,
    name: stage.label,
    total_operations: 0,
    completed_operations: 0,
    status:
      i < activeIndex ? 'finished' : i === activeIndex ? 'started' : 'pending',
  }));

const PreparingState: React.FC<PreparingStateProps> = ({ progress }) => {
  const phase = progress?.propose_phase;
  const activeIndex = STAGES.findIndex((s) => s.key === phase);
  // No usable marker yet (first poll not back, or an unrecognized phase) —
  // spinner + title, no rows.
  if (activeIndex < 0) {
    return <ProcessingPanel title={PREPARING_HEADING} />;
  }
  return (
    <ProcessingPanel title={PREPARING_HEADING} rows={stageRows(activeIndex)} />
  );
};

export default PreparingState;
